import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useFavorites } from '@/hooks/useFavorites';
import { QuoteCard } from '@/components/quote/QuoteCard';
import { QuoteSkeleton, Skeleton } from '@/components/ui/Skeleton';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export function AuthorPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading } = useQuery<{ success: boolean; data: any }>({
    queryKey: ['author', slug],
    queryFn: () => api.authors.getBySlug(slug!),
    enabled: !!slug,
  });
  const { data: favData } = useFavorites();

  const author = data?.data;
  const quotes = author?.quotes || [];
  const favorites = new Set((favData?.data || []).map((f: any) => f.quote_id || f.id));

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12">
        <Skeleton className="h-10 w-48 mb-8" />
        <Skeleton className="h-32 rounded-3xl mb-8" />
        <div className="grid md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <QuoteSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (!author) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <p className="text-xl text-surface-500">Author not found</p>
        <Link to="/authors" className="btn-primary mt-4 inline-flex">Browse Authors</Link>
      </div>
    );
  }

  return (
    <motion.div initial="initial" animate="animate" className="max-w-6xl mx-auto px-4 py-12">
      <motion.div variants={fadeUp}>
        <Link to="/authors" className="inline-flex items-center gap-2 text-sm text-surface-500 hover:text-surface-900 transition-colors mb-6">
          <ArrowLeft size={16} />
          All Authors
        </Link>
      </motion.div>

      {/* Author Header */}
      <motion.div variants={fadeUp} className="glass-card p-8 mb-8 flex items-center gap-6">
        <div className="w-20 h-20 rounded-full gradient-bg flex items-center justify-center text-white font-bold text-3xl shrink-0">
          {author.name?.charAt(0)}
        </div>
        <div>
          <h1 className="text-3xl font-bold mb-1">{author.name}</h1>
          <p className="text-surface-500">{quotes.length} quotes</p>
          {author.bio && (
            <p className="text-surface-600 mt-3 leading-relaxed">{author.bio}</p>
          )}
        </div>
      </motion.div>

      {/* Author's Quotes */}
      <div className="grid md:grid-cols-2 gap-4">
        {quotes.map((quote: any, index: number) => (
          <motion.div
            key={quote.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Link to={`/quote/${quote.id}`}>
              <QuoteCard quote={{ ...quote, author_name: author.name, author_slug: author.slug }} isFavorited={favorites.has(quote.id)} />
            </Link>
          </motion.div>
        ))}
      </div>

      {quotes.length === 0 && (
        <div className="text-center py-16">
          <p className="text-xl text-surface-500">No quotes from this author yet</p>
        </div>
      )}
    </motion.div>
  );
}
